// ***********************************
// AWS Transform (ATX) JSON export
// ***********************************
//
// Prototype export of DBCSI data into the ATX migration inventory format, following the "hybrid" proposal in
// ATX/proposal/ (Hybrid_database-instance.schema.json, server.schema.json, dbcsi_field_mapping.csv):
//   - one database_instance record per DBCSI collection (an Oracle database; RAC instances are already aggregated)
//   - one server record per host the databases run on
// Named performance fields hold the sizing percentiles.  Generic lists hold the Oracle long tail
// (Exadata metrics, features, object counts, parameters, other attributes, diagnostics).
// List item names follow dbcsi_field_mapping.csv.  Absent values mean unknown, as in the ATX schemas.

const ATX_SCHEMA_VERSION = "v1.1.0-dbcsi-proposal";

// [performance field, DBCSI field(s) in priority order, unit conversion]
// Older miner versions (before 4.0.11.aws.65) named the CPU peak fields without the P (e.g. DB_CPU_THREADS_PEAKS_90).
const ATX_PERFORMANCE_FIELDS = [
  ["cpu_threads_average_p99", ["DB_CPU_THREADS_AVERAGE_P99"]],
  ["cpu_threads_average_p95", ["DB_CPU_THREADS_AVERAGE_P95"]],
  ["cpu_threads_average_p75", ["DB_CPU_THREADS_AVERAGE_P75"]],
  ["cpu_threads_average_p50", ["DB_CPU_THREADS_AVERAGE_P50"]],
  ["cpu_threads_peak_p50", ["DB_CPU_THREADS_PEAKS_P50", "DB_CPU_THREADS_PEAKS_50"]],
  ["cpu_threads_peak_p90", ["DB_CPU_THREADS_PEAKS_P90", "DB_CPU_THREADS_PEAKS_90"]],
  ["cpu_threads_peak_p97", ["DB_CPU_THREADS_PEAKS_P97"]],
  ["host_cpu_threads_average_p99", ["DB_HOST_CPU_THREADS_AVERAGE_P99"]],
  ["host_cpu_threads_peak_p50", ["DB_HOST_CPU_THREADS_PEAKS_P50", "DB_HOST_CPU_THREADS_PEAKS_50"]],
  ["host_cpu_threads_peak_p90", ["DB_HOST_CPU_THREADS_PEAKS_P90", "DB_HOST_CPU_THREADS_PEAKS_90"]],
  ["iops_average_p99", ["DB_IOPS_AVERAGE_P99"]],
  ["iops_peak_p50", ["DB_IOPS_PEAKS_P50"]],
  ["iops_peak_p90", ["DB_IOPS_PEAKS_P90"]],
  ["iops_peak_p97", ["DB_IOPS_PEAKS_P97"]],
  ["throughput_average_p99_mibps", ["DB_IOMB_AVERAGE_P99"]],
  ["throughput_peak_p50_mibps", ["DB_IOMB_PEAKS_P50"]],
  ["throughput_peak_p90_mibps", ["DB_IOMB_PEAKS_P90"]],
  ["throughput_peak_p97_mibps", ["DB_IOMB_PEAKS_P97"]],
  ["redo_average_p99_mibps", ["DB_REDO_MB_AVERAGE_P99"]],
  ["redo_peak_p50_mibps", ["DB_REDO_MB_PEAKS_P50"]],
  ["redo_peak_p90_mibps", ["DB_REDO_MB_PEAKS_P90"]],
  ["sga_pga_total_mebibytes", ["DB_SGAPGA_TOTAL"], 1024],
  ["sga_average_mebibytes", ["DB_SGA_AVG"], 1024],
  ["pga_max_mebibytes", ["DB_PGA_MAX"], 1024],
  ["pdb_cpu_threads_average_p99", ["DB_PDB2_CPU_THREADS_AVERAGE_P99"]],
  ["pdb_cpu_threads_average_p75", ["DB_PDB2_CPU_THREADS_AVERAGE_P75"]],
  ["pdb_throughput_average_p99_mibps", ["DB_PDB2_IOMB_AVERAGE_P99"]]
];

// [engine_metrics name, DBCSI field, unit]  (all are P99 of the per-snapshot averages)
const ATX_EXADATA_METRICS = [
  ["exadata_cell_bytes_eligible", "DB_EXA_CELL_BYTES_ELIG_MB_P99", "mibps"],
  ["exadata_storage_index_saved", "DB_EXA_CELL_SI_SAVE_MB_P99", "mibps"],
  ["exadata_interconnect", "DB_EXA_CELL_INT_MB_P99", "mibps"],
  ["exadata_interconnect_smart_scan", "DB_EXA_CELL_INT_SS_MB_P99", "mibps"],
  ["exadata_hcc_decompressed", "DB_EXA_CELL_HCC_BYTES_MB_P99", "mibps"],
  ["exadata_read", "DB_EXA_READ_MB_P99", "mibps"],
  ["exadata_read_optimized", "DB_EXA_READ_MB_OPT_P99", "mibps"],
  ["exadata_multiblock_read_requests", "DB_EXA_READ_MULTI_P99", "requests_per_second"],
  ["exadata_temp_read", "DB_EXA_READ_TEMP_P99", "blocks_per_second"],
  ["exadata_temp_write", "DB_EXA_WRITE_TEMP_P99", "blocks_per_second"]
];

// [object_counts object_type, DBCSI field]
const ATX_OBJECT_COUNTS = [
  ["schemas", "COUNT_SCHEMAS"], ["tables", "COUNT_TABLE"], ["table_partitions", "COUNT_TABLE PARTITION"],
  ["indexes", "COUNT_INDEX"], ["index_partitions", "COUNT_INDEX PARTITION"], ["bitmap_indexes", "COUNT_BITMAP_INDEX"],
  ["views", "COUNT_VIEW"], ["materialized_views", "COUNT_MATERIALIZED VIEW"],
  ["materialized_views_rewrite_enabled", "COUNT_MATVIEWS_REWRITE_ENABLED"], ["sequences", "COUNT_SEQUENCE"],
  ["packages", "COUNT_PACKAGE"], ["procedures", "COUNT_PROCEDURE"], ["functions", "COUNT_FUNCTION"],
  ["triggers", "COUNT_TRIGGER"], ["types", "COUNT_TYPE"], ["type_bodies", "COUNT_TYPE BODY"], ["lobs", "COUNT_LOB"],
  ["plsql_lines", "COUNT_LINES_PLSQL"], ["db_links", "COUNT_DB_LINKS"], ["hcc_tables", "COUNT_TABLES_HCC"],
  ["vpd_policies", "COUNT_VPD_POLICIES"], ["tables_without_primary_key", "COUNT_TABLES_WITHOUT_PK"],
  ["scheduler_jobs_enabled", "COUNT_SCHEDULER_JOBS_ENABLED"], ["dbms_jobs_not_broken", "COUNT_DBMS_JOBS_NOT_BROKEN"]
];

// [configuration_parameters name, DBCSI field]
const ATX_PARAMETERS = [
  ["sga_target", "SGA_TARGET"], ["memory_target", "MEMORY_TARGET"], ["nls_comp", "NLS_COMP"], ["nls_sort", "NLS_SORT"],
  ["statistics_level", "DEBUG_statistics_level"], ["timed_statistics", "DEBUG_timed_statistics"],
  ["control_management_pack_access", "DEBUG_control_management_pack_access"],
  ["awr_pdb_autoflush_enabled", "DEBUG_awr_pdb_autoflush_enabled"],
  ["max_iops", "DEBUG_max_iops"], ["max_mbps", "DEBUG_max_mbps"]
];

// [engine_attributes name, DBCSI field, unit, conversion]
const ATX_ATTRIBUTES = [
  ["dbid", "DBID"], ["db_name", "DB_NAME"], ["db_unique_name", "DB_UNIQUE_NAME"], ["database_role", "DATABASE_ROLE"],
  ["platform_name", "PLATFORM_NAME"], ["banner", "BANNER"],
  ["instance_count", "INSTANCES", "", "number"], ["instance_count_current", "INSTANCES_CURRENT", "", "number"],
  ["hosts", "HOSTS"], ["hosts_current", "HOSTS_CURRENT"], ["host_connected", "HOST_CONNECTED"],
  ["instance_number_connected", "INSTANCE#_CONNECTED", "", "number"],
  ["is_pdb", "IS_PDB", "", "yesno"], ["pdb_name", "PDB_NAME"], ["pdb_dbid", "PDB_DBID"], ["con_id", "CON_ID", "", "number"],
  ["cpu_count_global", "GLOBAL_CPU_COUNT", "cpu_threads", "number"],
  ["cpu_core_count_global", "GLOBAL_CPU_CORE_COUNT", "cores", "number"],
  ["cpu_socket_count_global", "GLOBAL_CPU_SOCKET_COUNT", "sockets", "number"],
  ["datafile_size", "TOTAL_DATAFILE_SIZE_GB", "mebibytes", "gib"],
  ["tempfile_size", "TOTAL_TEMPFILE_SIZE_GB", "mebibytes", "gib"],
  ["used_size", "USED_DB_SIZE_GB", "mebibytes", "gib"],
  ["dataguard_config_count", "DATAGUARD_CONFIG_COUNT", "", "number"],
  ["is_rds", "IS_RDS", "", "yesno"], ["rds_local_listener", "LOCAL_LISTENER"],
  ["ebs_release", "IS_EBIZ"], ["jde_object_count", "IS_JDE", "", "number"]
];

// [diagnostics name, DBCSI field, conversion]
const ATX_DIAGNOSTICS = [
  ["statspack_snapshot_count", "STATSPACK_SNAPSHOTS", "number"], ["client_program", "DEBUG_PROGRAM"],
  ["client_module", "DEBUG_MODULE"], ["client_version", "DEBUG_SQLPLUS"], ["container", "DEBUG_CONTAINER"],
  ["dictionary_views", "DEBUG_CDB_OR_DBA_COL"]
];

// ***********************************
// helpers
// ***********************************

function isBlank(value) {
  return value === undefined || value === null || String(value).trim() === "" || String(value) === "NaN";
}

// numeric value (optionally scaled), or undefined if missing/not a number
function toNumber(value, scale) {
  if (isBlank(value) || isNaN(value * 1)) return undefined;
  const n = value * (scale || 1);
  return Math.round(n * 1000) / 1000;
}

function firstValue(sources, keys) {
  for (const key of keys) {
    for (const src of sources) {
      if (src && !isBlank(src[key])) return src[key];
    }
  }
  return undefined;
}

function convert(value, conversion) {
  if (isBlank(value)) return undefined;
  if (conversion == "number") return toNumber(value);
  if (conversion == "gib") return toNumber(value, 1024);
  if (conversion == "yesno") return String(value).toUpperCase() == "YES";
  return String(value);
}

// drop undefined properties so absent fields stay absent (ATX: absent means unknown)
function compact(obj) {
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = v;
  }
  return out;
}

function splitHosts(value) {
  if (isBlank(value)) return [];
  return String(value).split(",").map(h => h.trim()).filter(h => h != "");
}

function serverIdFor(host) {
  return "server-" + host.toLowerCase().replace(/[^a-z0-9_.-]/g, "_");
}

function getEdition(banner) {
  if (isBlank(banner)) return undefined;
  if (banner.includes("Enterprise Edition")) return "Enterprise Edition";
  if (banner.includes("Standard Edition 2")) return "Standard Edition 2";
  if (banner.includes("Standard Edition")) return "Standard Edition";
  return undefined;
}

function getServerRole(databaseRole) {
  if (isBlank(databaseRole)) return undefined;
  if (databaseRole == "PRIMARY") return "primary";
  if (databaseRole.includes("STANDBY")) return "secondary";
  return undefined;
}

// Oracle PLATFORM_NAME -> ATX operating_system_type and a CPU architecture family
function getPlatformInfo(platformName) {
  const p = String(platformName || "").toLowerCase();
  if (p == "") return {};
  let osType = "Other";
  if (p.includes("linux")) osType = "Linux";
  else if (p.includes("windows")) osType = "Windows";
  let arch;
  if (p.includes("x86") || p.includes("x64") || p.includes("amd64")) arch = "x86_64";
  else if (p.includes("aix") || p.includes("power")) arch = "power";
  else if (p.includes("solaris") && !p.includes("x86")) arch = "sparc";
  else if (p.includes("itanium") || p.includes("hp-ux ia")) arch = "itanium";
  else if (p.includes("arm") || p.includes("aarch64")) arch = "arm64";
  return { osType, arch };
}

// "FEATURE_<name>" keys can be truncated to 60 characters by the upload lambda; report what was stored
function getFeatures(genInfo) {
  return Object.keys(genInfo)
    .filter(key => key.startsWith("FEATURE_") && genInfo[key] == "Yes")
    .sort()
    .map(key => ({ name: key.substring(8), in_use: true, source_key: key }));
}

/*

Summary:
The function `build_atx_records` converts the DBCSI databases in one scenario into ATX server and database_instance records.

Description:
1. Builds one database_instance per collection: existing ATX fields, named performance fields, and the generic lists
2. Builds one server per host (from HOSTS), shared by every database that runs on it.  DBCSI only knows CPU and memory
   for the host the collector connected to (HOST_CONNECTED).
3. Prefers the miner's own summary values (genInfo) and falls back to the values DBCSI computed from the time series
   (additional_datapoints) for older collections that did not output a field

Parameters:
1. items (array): batchItems rows for the scenario (includes collectionID, additional_datapoints)
2. genInfoById (object): collectionID -> name/value map of the collection's general info (GET /dbcsi/:id/ora_v1)
3. options (object): { updatedTimestamp (seconds) }

Returns { databaseInstances: [...], servers: [...] }.

*/

function build_atx_records(items, genInfoById, options) {
  const opts = options || {};
  const updatedTimestamp = opts.updatedTimestamp || Math.floor(Date.now() / 1000);
  const servers = new Map();
  const databaseInstances = [];
  const seen = new Set();

  for (const item of items) {
    if (item.collectionID === undefined || seen.has(item.collectionID)) continue;
    seen.add(item.collectionID);
    const g = genInfoById[item.collectionID] || {};
    const adp = item.additional_datapoints || {};
    const sourceFile = `collection_${item.collectionID}_metrics.out`;
    const collector = !isBlank(g.STATSPACK_MINER_VER) ? "dbcsi_statspack_miner" : "dbcsi_awr_miner";
    const collectorVersion = firstValue([g], ["AWR_MINER_VER", "STATSPACK_MINER_VER"]);
    const dbId = "db-instance-dbcsi-" + item.collectionID;

    // servers: one per host
    let hosts = splitHosts(g.HOSTS);
    if (hosts.length == 0 && !isBlank(g.HOST_CONNECTED)) hosts = [String(g.HOST_CONNECTED)];
    const platform = getPlatformInfo(g.PLATFORM_NAME);
    const numInstances = toNumber(firstValue([g, item], ["INSTANCES", "NUM_INSTANCES"]));
    const isClustered = numInstances === undefined ? undefined : numInstances > 1;
    for (const host of hosts) {
      const id = serverIdFor(host);
      let server = servers.get(id);
      if (!server) {
        server = {
          id: id, assetType: "server", external_id: id, updatedTimestamp: updatedTimestamp, schema_version: ATX_SCHEMA_VERSION,
          data_collection_source: "dbcsi", source_files: [], source_file_formats: [collector],
          primary_hostname: host, operating_system_type: platform.osType, cpu_architecture: platform.arch,
          oracle_platform_name: convert(g.PLATFORM_NAME), is_clustered: isClustered, database_instance_ids: []
        };
        servers.set(id, server);
      }
      server.database_instance_ids.push(dbId);
      if (!server.source_files.includes(sourceFile)) server.source_files.push(sourceFile);
      if (!server.source_file_formats.includes(collector)) server.source_file_formats.push(collector);
      if (host == g.HOST_CONNECTED) {
        // only the connected host's CPU and memory are known; keep the first database's values
        if (server.cpu_thread_count === undefined) server.cpu_thread_count = toNumber(g.NUM_CPUS);
        if (server.total_cpu_cores === undefined) server.total_cpu_cores = toNumber(g.NUM_CPU_CORES);
        if (server.cpu_socket_count === undefined) server.cpu_socket_count = toNumber(g.NUM_CPU_SOCKETS);
        if (server.total_system_memory_mebibytes === undefined) server.total_system_memory_mebibytes = toNumber(g.PHYSICAL_MEMORY_GB, 1024);
        if (server.free_memory_mebibytes === undefined) server.free_memory_mebibytes = toNumber(g.FREE_MEMORY_GB, 1024);
      }
    }

    // named performance fields
    const performance = {};
    for (const [field, keys, scale] of ATX_PERFORMANCE_FIELDS) {
      // memory fields keep the miner's own definitions, so only use the miner values for them
      const sources = scale ? [g] : [g, adp];
      const value = toNumber(firstValue(sources, keys), scale);
      if (value !== undefined) performance[field] = value;
    }

    // generic lists
    const engineMetrics = [];
    for (const [name, key, unit] of ATX_EXADATA_METRICS) {
      const value = toNumber(firstValue([g, adp], [key]));
      if (value !== undefined) {
        engineMetrics.push({ name: name, statistic: "average", percentile: 99, value: value, unit: unit, scope: "database", source_key: key });
      }
    }
    const objectCounts = [];
    for (const [objectType, key] of ATX_OBJECT_COUNTS) {
      const value = toNumber(g[key]);
      if (value !== undefined) objectCounts.push({ object_type: objectType, count: value, source_key: key });
    }
    const parameters = [];
    for (const [name, key] of ATX_PARAMETERS) {
      if (!isBlank(g[key])) parameters.push({ name: name, value: String(g[key]), source_key: key });
    }
    const attributes = [];
    for (const [name, key, unit, conversion] of ATX_ATTRIBUTES) {
      const value = convert(g[key], conversion);
      if (value !== undefined) attributes.push(compact({ name: name, value: value, unit: unit || undefined, source_key: key }));
    }
    for (const key of Object.keys(g).filter(k => k.startsWith("EBIZ_NODE_")).sort()) {
      attributes.push({ name: "ebs_node_" + key.substring(10).toLowerCase(), value: String(g[key]), source_key: key });
    }
    const diagnostics = [];
    for (const [name, key, conversion] of ATX_DIAGNOSTICS) {
      const value = convert(g[key], conversion);
      if (value !== undefined) diagnostics.push({ name: name, value: value, source_key: key });
    }
    for (const key of Object.keys(g).sort()) {
      if (key.startsWith("ERROR:")) diagnostics.push({ name: "error_" + key.substring(6).toLowerCase(), value: String(g[key]), source_key: key });
      const timing = key.match(/^DEBUG_(.+)_TIME$/);
      if (timing) diagnostics.push({ name: timing[1].toLowerCase() + "_seconds", value: toNumber(g[key]), unit: "seconds", source_key: key });
    }

    const isPdb = String(firstValue([g, item], ["IS_PDB"]) || "").toUpperCase() == "YES";
    const countPdbs = toNumber(g.COUNT_PDBS);
    const serverIds = hosts.map(serverIdFor);
    databaseInstances.push(compact({
      id: dbId,
      assetType: "database_instance",
      external_id: "dbcsi-collection-" + item.collectionID,
      updatedTimestamp: updatedTimestamp,
      source_files: [sourceFile],
      source_file_formats: [collector],
      schema_version: ATX_SCHEMA_VERSION,
      engine_type: "oracle",
      instance_id: isBlank(g.DBID) ? undefined : String(g.DBID),
      instance_name: firstValue([g, item], ["DB_NAME", "database"]),
      version: firstValue([g, item], ["VERSION"]),
      edition: getEdition(firstValue([g, item], ["BANNER"])),
      is_clustered: isClustered,
      server_role: getServerRole(firstValue([g, item], ["DATABASE_ROLE"])),
      databases_count: (!isPdb && countPdbs !== undefined && countPdbs >= 0) ? countPdbs : undefined,
      total_database_size_mebibytes: toNumber(firstValue([g, adp, item], ["TOTAL_DB_SIZE_GB"]), 1024),
      server_id: serverIds.length > 0 ? serverIds[0] : undefined,
      server_ids: serverIds.length > 0 ? serverIds : undefined,
      collection_source: compact({ collector: collector, collector_version: collectorVersion }),
      performance: performance,
      engine_metrics: engineMetrics,
      features: getFeatures(g),
      object_counts: objectCounts,
      configuration_parameters: parameters,
      engine_attributes: attributes,
      diagnostics: diagnostics
    }));
  }

  return { databaseInstances: databaseInstances, servers: [...servers.values()].map(compact) };
}

export { build_atx_records };
